/**
 * ResultsPage component for displaying assessment results
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssessmentContext } from '../context/AssessmentContext';
import EnhancedResultsDashboard from '../components/EnhancedResultsDashboard';
import * as apiService from '../services/apiService';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { MaturityLevel } from '../models/Result';
import './results-print.css';

// Function to determine maturity level based on score
const determineMaturityLevelFromScore = (score: number): MaturityLevel => {
  // Using raw score ranges:
  // Build: 20-39
  // Elevate: 40-59
  // Optimize: 60-80
  if (score >= 60) {
    return 'optimize';
  } else if (score >= 40) {
    return 'elevate';
  }
  return 'build';
};

const ResultsPage: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { state } = useAssessmentContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<any | null>(null);
  const [resultsData, setResultsData] = useState<any | null>(null);
  const [categoryScores, setCategoryScores] = useState<any[]>([]);
  const [strengths, setStrengths] = useState<string[]>([]);
  const [weaknesses, setWeaknesses] = useState<string[]>([]);
  
  // Fetch assessment results from API
  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!assessmentId) {
        console.error('No assessment ID provided');
        setError('Assessment ID is missing. Please try again.');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching assessment data for ID:', assessmentId);
        
        // Try to get assessment data from API
        const assessmentData = await apiService.getAssessment(assessmentId);
        console.log('CASCADE_DEBUG: Raw assessmentData from apiService.getAssessment:', assessmentData); 
        console.log('Assessment data retrieved:', assessmentData);
        
        if (!assessmentData) {
          console.log('No assessment data found in API, checking context');
          // If no assessment found in API, check context
          if (state.assessment && state.assessment.id === assessmentId) {
            console.log('Using assessment data from context');
            setAssessment(state.assessment);
          } else {
            console.error('No assessment data available');
            setError('Assessment not found. Please try again.');
            setLoading(false);
            return;
          }
        } else {
          // Use data from API
          console.log('Using assessment data from API');
          setAssessment(assessmentData);
        }
        
        // Get results from API
        console.log('Fetching results for assessment ID:', assessmentId);
        const results = await apiService.getResults(assessmentId);
        console.log('Results data retrieved:', results);
        console.log('Results data type:', typeof results);
        console.log('Results data structure:', JSON.stringify(results, null, 2));
        
        setResultsData(results);
        
        if (results && results.categoryScores) {
          console.log('Setting category scores from results.categoryScores');
          setCategoryScores(results.categoryScores);
        } else if (results && Array.isArray(results) && results.length > 0) {
          console.log('Setting category scores from results array');
          setCategoryScores(results);
          
          // Identify strengths and weaknesses
          const sortedScores = [...results].sort((a, b) => b.percentage - a.percentage);
          setStrengths(sortedScores.slice(0, 3).map(s => s.categoryName));
          setWeaknesses(sortedScores.slice(-3).map(s => s.categoryName));
        } else {
          console.log('No category results found, trying to calculate');
          // If we have responses in context but no results, try to calculate them
          if (Object.keys(state.responses).length > 0 && state.assessment && state.assessment.id === assessmentId) {
            console.log('Calculating results from responses in context');
            try {
              // Save responses and calculate results
              await apiService.saveResponses(assessmentId, state.responses);
              const updatedAssessment = await apiService.saveResults(assessmentId, assessmentData.userId, state.responses);
              setAssessment(updatedAssessment);
              
              // Get newly calculated results
              const newResults = await apiService.getResults(assessmentId);
              setResultsData(newResults);
              
              if (newResults && newResults.categoryScores) {
                setCategoryScores(newResults.categoryScores);
                
                // Identify strengths and weaknesses
                const sortedScores = [...newResults.categoryScores].sort((a, b) => b.percentage - a.percentage);
                setStrengths(sortedScores.slice(0, 3).map(s => s.categoryName));
                setWeaknesses(sortedScores.slice(-3).map(s => s.categoryName));
              } else if (newResults && newResults.categoryResults) {
                setCategoryScores(newResults.categoryResults);
                
                // Identify strengths and weaknesses
                const sortedScores = [...newResults.categoryResults].sort((a, b) => b.percentage - a.percentage);
                setStrengths(sortedScores.slice(0, 3).map(s => s.categoryName));
                setWeaknesses(sortedScores.slice(-3).map(s => s.categoryName));
              } else {
                setError('Unable to calculate assessment results. Please try again.');
              }
            } catch (error) {
              console.error('Error calculating assessment results:', error);
              setError('Failed to calculate results. Please try again.');
            }
          } else {
            setError('No results found for this assessment.');
          }
        }
      } catch (err) {
        console.error('Error fetching assessment data:', err);
        setError('Failed to load assessment results. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAssessmentData();
  }, [assessmentId, state.user, state.assessment, state.responses, navigate]);

  
  const handleDownloadPdf = () => {
    // The closest we can get to "File → Export as PDF" is window.print()
    // with the destination automatically set to PDF in most browsers
    
    // Check if browser supports automatic PDF destination
    const isChrome = /Chrome/.test(navigator.userAgent);
    const isEdge = /Edge|Edg/.test(navigator.userAgent);
    
    if (isChrome || isEdge) {
      // Chrome/Edge users can set "Save as PDF" as default destination
      // This will respect their printer settings
      window.print();
    } else {
      // For other browsers, provide helpful guidance
      const message = navigator.platform.includes('Mac') 
        ? 'Press Cmd+P, then select "Save as PDF" for best results.'
        : 'Press Ctrl+P, then select "Save as PDF" for best results.';
      
      if (confirm(`${message}\n\nClick OK to open print dialog.`)) {
        window.print();
      }
    }
  };
  
  const handleStartOver = () => {
    // Navigate back to contact page
    navigate('/assessment/contact');
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="container mx-auto px-4 pb-16 pt-32 md:pt-36 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-[#2A9D8F] mb-4" />
        <p className="text-lg text-gray-600">Loading your assessment results...</p>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="container mx-auto px-4 pb-16 pt-32 md:pt-36">
        <div className="max-w-4xl mx-auto bg-red-50 p-6 rounded-lg border border-red-200">
          <h1 className="text-2xl font-bold text-red-700 mb-4">Error Loading Results</h1>
          <p className="text-gray-700 mb-6">{error}</p>
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={handleStartOver}
            >
              Return to Assessment
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // If no assessment data available
  if (!assessment && !state.assessment) {
    return null; // Will redirect via useEffect
  }
  
  // Use assessment data from API or context
  const assessmentData = assessment || state.assessment;
  // const overallScore = assessmentData?.overallScore || 0; // This will be passed directly from assessmentData
  // const maturityLevel = assessmentData?.maturityLevel || 'build'; // This will be passed directly from assessmentData
  
  return (
    <div className="container mx-auto px-4 pb-16 pt-32 md:pt-36">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Your PMO Maturity Results
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Thank you for completing the assessment. Below are your results and tailored recommendations
            to help improve your PMO maturity.
          </p>
        </div>
        
        <div className="space-y-12">
          <EnhancedResultsDashboard 
            categoryResults={categoryScores} // categoryScores state already holds API's categoryResults
            overallScorePercentage={resultsData?.totalScore || 0} // Use actual score from API
            maturityLevel={determineMaturityLevelFromScore(resultsData?.totalScore || 0)} // Calculate maturity level based on score
            onDownloadPdf={handleDownloadPdf}
          />
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
